import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Button,
  Grid,
  Autocomplete,
  TextField,
  Chip,
  Snackbar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Save,
  ContentCopy,
  ArrowBack,
} from '@mui/icons-material';
import { useGetAgentsForAccountQuery, useToggleAgentWebhookMutation } from '../store/api/synthflowAgentsApi';
import { useGetActiveSynthflowAccountsQuery } from '../store/api/synthflowApi';
import {
  useGetEmailAddressesQuery,
  useGetAgentEmailAssignmentsQuery,
  useUpdateAgentEmailAssignmentsMutation,
  EmailAddressDropdown,
  UpdateAgentEmailAssignmentsRequest
} from '../store/api/emailAddressesApi';

export const AgentCallSummaryPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const agentIdNumber = agentId ? parseInt(agentId, 10) : null;

  // Email address management state
  const [toEmails, setToEmails] = useState<EmailAddressDropdown[]>([]);
  const [ccEmails, setCcEmails] = useState<EmailAddressDropdown[]>([]);
  const [bccEmails, setBccEmails] = useState<EmailAddressDropdown[]>([]);

  // Save button state management
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Sync feedback state
  const [originalAssignments, setOriginalAssignments] = useState<{
    to: EmailAddressDropdown[];
    cc: EmailAddressDropdown[];
    bcc: EmailAddressDropdown[];
  } | null>(null);

  // Fetch accounts to find which account this agent belongs to
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    error: accountsError
  } = useGetActiveSynthflowAccountsQuery();

  // Find the account that contains this agent
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<number | null>(null);

  // Fetch agents for the selected account
  const {
    data: agents = [],
    isLoading: agentsLoading,
    error: agentsError
  } = useGetAgentsForAccountQuery(selectedAccount!, {
    skip: !selectedAccount
  });

  // Email address management hooks
  const {
    data: availableEmails = [],
    isLoading: emailsLoading,
  } = useGetEmailAddressesQuery();

  // Fetch agent email assignments when an agent is selected
  const {
    data: agentEmailAssignments,
  } = useGetAgentEmailAssignmentsQuery(selectedAgent!, {
    skip: !selectedAgent
  });

  const [updateAgentEmailAssignments] = useUpdateAgentEmailAssignmentsMutation();

  // Webhook management hooks
  const [toggleWebhook, { isLoading: toggleWebhookLoading }] = useToggleAgentWebhookMutation();
  
  // Webhook toggle success/error state
  const [webhookToggleMessage, setWebhookToggleMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  
  // Local state to track webhook enabled status for immediate UI updates
  const [localWebhookEnabled, setLocalWebhookEnabled] = useState<{ [agentId: number]: boolean }>({});

  // Helper function to get webhook enabled status (local state takes precedence)
  const getWebhookEnabledStatus = (agentId: number, defaultStatus: boolean): boolean => {
    return localWebhookEnabled[agentId] !== undefined ? localWebhookEnabled[agentId] : defaultStatus;
  };

  // Find the account and agent based on agentId
  useEffect(() => {
    if (agentIdNumber && accounts.length > 0) {
      // We need to find which account contains this agent
      // For now, we'll try each account until we find the agent
      const findAgentInAccounts = async () => {
        for (const account of accounts) {
          try {
            // This would need to be implemented as a query that checks if agent exists in account
            // For now, we'll assume the first account contains the agent
            setSelectedAccount(account.id);
            setSelectedAgent(agentIdNumber);
            break;
          } catch (error) {
            console.log(`Agent ${agentIdNumber} not found in account ${account.id}`);
          }
        }
      };
      findAgentInAccounts();
    }
  }, [agentIdNumber, accounts]);

  // Load agent email assignments when agent changes
  useEffect(() => {
    if (agentEmailAssignments) {
      setToEmails(agentEmailAssignments.to_emails);
      setCcEmails(agentEmailAssignments.cc_emails);
      setBccEmails(agentEmailAssignments.bcc_emails);

      // Store original assignments for change detection
      setOriginalAssignments({
        to: agentEmailAssignments.to_emails,
        cc: agentEmailAssignments.cc_emails,
        bcc: agentEmailAssignments.bcc_emails
      });
    } else {
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);
      setOriginalAssignments({
        to: [],
        cc: [],
        bcc: []
      });
    }
  }, [agentEmailAssignments]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!originalAssignments) return false;

    const toChanged = JSON.stringify(toEmails.map(e => e.id).sort()) !==
      JSON.stringify(originalAssignments.to.map(e => e.id).sort());
    const ccChanged = JSON.stringify(ccEmails.map(e => e.id).sort()) !==
      JSON.stringify(originalAssignments.cc.map(e => e.id).sort());
    const bccChanged = JSON.stringify(bccEmails.map(e => e.id).sort()) !==
      JSON.stringify(originalAssignments.bcc.map(e => e.id).sort());

    return toChanged || ccChanged || bccChanged;
  };

  const handleSaveEmailAssignments = async () => {
    if (!selectedAgent) return;

    setIsSaving(true);
    try {
      const assignmentsData: UpdateAgentEmailAssignmentsRequest = {
        to_emails: toEmails.map(email => email.id),
        cc_emails: ccEmails.map(email => email.id),
        bcc_emails: bccEmails.map(email => email.id)
      };

      await updateAgentEmailAssignments({
        agentId: selectedAgent,
        data: assignmentsData
      }).unwrap();

      // Update original assignments to reflect saved state
      setOriginalAssignments({
        to: toEmails,
        cc: ccEmails,
        bcc: bccEmails
      });

      // Show success message
      setShowSuccessMessage(true);

    } catch (error) {
      console.error('Failed to save email assignments:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToAgents = () => {
    navigate('/aigent/agents');
  };

  const handleWebhookToggle = async () => {
    if (!selectedAgent || !currentAgent) return;

    const webhookEnabled = getWebhookEnabledStatus(selectedAgent, currentAgent.webhook_enabled);
    
    try {
      console.log('🔄 Starting webhook toggle:', {
        agentId: selectedAgent,
        agentName: currentAgent.name,
        currentEnabled: webhookEnabled,
        newEnabled: !webhookEnabled
      });
      
      await toggleWebhook({ 
        agentId: selectedAgent, 
        enabled: !webhookEnabled 
      }).unwrap();
      
      // Update local state immediately for responsive UI
      setLocalWebhookEnabled(prev => ({
        ...prev,
        [selectedAgent]: !webhookEnabled
      }));
      
      // Show success message
      setWebhookToggleMessage({
        type: 'success',
        message: `Webhook ${!webhookEnabled ? 'enabled' : 'disabled'} successfully for ${currentAgent.name}`
      });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setWebhookToggleMessage(null);
      }, 5000);
      
    } catch (error: any) {
      console.error('❌ Webhook toggle failed:', error);
      setWebhookToggleMessage({
        type: 'error',
        message: `Failed to ${!webhookEnabled ? 'enable' : 'disable'} webhook: ${error?.data?.detail || error?.message || 'Unknown error'}`
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setWebhookToggleMessage(null);
      }, 5000);
    }
  };

  if (accountsLoading || agentsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (accountsError || agentsError) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/aigent')}
            sx={{ textDecoration: 'none' }}
          >
            Aigent
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={handleBackToAgents}
            sx={{ textDecoration: 'none' }}
          >
            Agents
          </Link>
          <Typography color="text.primary">Call Summary Configuration</Typography>
        </Breadcrumbs>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Failed to load agent data
          </Typography>
          <Typography variant="body2" gutterBottom>
            Error details: {
              (accountsError as any)?.data?.detail ||
              (agentsError as any)?.data?.detail ||
              'Unknown error'
            }
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!selectedAgent) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/aigent')}
            sx={{ textDecoration: 'none' }}
          >
            Aigent
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={handleBackToAgents}
            sx={{ textDecoration: 'none' }}
          >
            Agents
          </Link>
          <Typography color="text.primary">Call Summary Configuration</Typography>
        </Breadcrumbs>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Agent not found
          </Typography>
          <Typography variant="body2">
            The requested agent could not be found or you don't have access to it.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const currentAgent = agents.find(agent => agent.id === selectedAgent);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/aigent')}
          sx={{ textDecoration: 'none' }}
        >
          Aigent
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToAgents}
          sx={{ textDecoration: 'none' }}
        >
          Agents
        </Link>
        <Typography color="text.primary">
          {currentAgent?.name || 'Call Summary Configuration'}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Call Summary Configuration
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Configure call summary email notifications for{' '}
            <Typography 
              component="span" 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 'bold', 
                color: 'primary.main',
                backgroundColor: 'primary.light',
                px: 1,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {currentAgent?.name || 'this agent'}
            </Typography>
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToAgents}
        >
          Back to Agents
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Configure who receives call summary emails for this agent.
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Autocomplete
                  multiple
                  options={availableEmails}
                  value={toEmails}
                  onChange={(_, newValue) => setToEmails(newValue)}
                  getOptionLabel={(option) => option.email}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="TO Recipients"
                      placeholder="Select email addresses"
                      helperText="Primary recipients of call summary emails"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.email}
                        size="small"
                      />
                    ))
                  }
                  disabled={emailsLoading}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  multiple
                  options={availableEmails}
                  value={ccEmails}
                  onChange={(_, newValue) => setCcEmails(newValue)}
                  getOptionLabel={(option) => option.email}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="CC Recipients"
                      placeholder="Select email addresses"
                      helperText="Carbon copy recipients"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.email}
                        size="small"
                      />
                    ))
                  }
                  disabled={emailsLoading}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  multiple
                  options={availableEmails}
                  value={bccEmails}
                  onChange={(_, newValue) => setBccEmails(newValue)}
                  getOptionLabel={(option) => option.email}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BCC Recipients"
                      placeholder="Select email addresses"
                      helperText="Blind carbon copy recipients"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.email}
                        size="small"
                      />
                    ))
                  }
                  disabled={emailsLoading}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveEmailAssignments}
                  disabled={isSaving || !hasUnsavedChanges()}
                  fullWidth
                >
                  {isSaving ? 'Saving...' : 'Save Email Configuration'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Agent Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Agent Name:</strong> {currentAgent?.name || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Phone Number:</strong> {currentAgent?.phone_number || 'Not configured'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {currentAgent?.type || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Webhook Status:</strong> {getWebhookEnabledStatus(selectedAgent!, currentAgent?.webhook_enabled || false) ? 'Enabled' : 'Disabled'}
                </Typography>
              </Box>

              {webhookToggleMessage && (
                <Alert 
                  severity={webhookToggleMessage.type} 
                  sx={{ mb: 2 }}
                  onClose={() => setWebhookToggleMessage(null)}
                >
                  {webhookToggleMessage.message}
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                <Button
                  variant={getWebhookEnabledStatus(selectedAgent!, currentAgent?.webhook_enabled || false) ? "outlined" : "contained"}
                  color={getWebhookEnabledStatus(selectedAgent!, currentAgent?.webhook_enabled || false) ? "error" : "primary"}
                  onClick={handleWebhookToggle}
                  disabled={toggleWebhookLoading}
                  fullWidth
                >
                  {toggleWebhookLoading ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null}
                  {toggleWebhookLoading 
                    ? (getWebhookEnabledStatus(selectedAgent!, currentAgent?.webhook_enabled || false) ? "Disabling..." : "Enabling...")
                    : (getWebhookEnabledStatus(selectedAgent!, currentAgent?.webhook_enabled || false) ? "Disable Webhook" : "Enable Webhook")
                  }
                </Button>
              </Box>

              {currentAgent?.webhook_url && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Webhook URL
                  </Typography>
                  <TextField
                    fullWidth
                    value={currentAgent.webhook_url}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => navigator.clipboard.writeText(currentAgent.webhook_url || '')}
                            edge="end"
                          >
                            <ContentCopy />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        message="Email configuration saved successfully!"
      />
    </Box>
  );
};
